import type { LayerState } from '@vitals/shared';
import type { VitalDef, VitalTone } from './vitals.js';

/**
 * Map a US EPA AQI value to its category and the panel's three-level tone.
 * EPA's six categories collapse onto good/warn/bad: 0–50 good, 51–150 warn,
 * 151+ bad.
 */
export function aqiBand(aqi: number): { category: string; tone: VitalTone } {
  if (aqi <= 50) return { category: 'Good', tone: 'good' };
  if (aqi <= 100) return { category: 'Moderate', tone: 'warn' };
  if (aqi <= 150) return { category: 'Unhealthy for Sensitive Groups', tone: 'warn' };
  if (aqi <= 200) return { category: 'Unhealthy', tone: 'bad' };
  if (aqi <= 300) return { category: 'Very Unhealthy', tone: 'bad' };
  return { category: 'Hazardous', tone: 'bad' };
}

/**
 * Vital selector for the Open-Meteo air-quality layer (NYC US AQI).
 *
 * Display: value "31" / label "air quality", tinted by tone.
 * Down/no-summary: value "—", label "air quality", down true.
 */
export const aqiVital: VitalDef = {
  id: 'aqi',
  layerId: 'aqi',

  select(s: LayerState | undefined) {
    const isDown = !s || s.status === 'down' || s.summary == null;

    if (isDown) {
      return {
        value: '—',
        label: 'air quality',
        down: true,
        stale: false,
      };
    }

    const stale = s.status === 'stale';

    const summary = s.summary as { aqi: number; pm25: number };
    const aqi = Math.round(summary.aqi);
    const { tone } = aqiBand(aqi);

    return {
      value: `${aqi}`,
      label: 'air quality',
      down: false,
      stale,
      tone,
    };
  },
};
